import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Users2,
  Search,
  CheckCircle2,
  UserPlus2,
  UserMinus2,
  X,
  Undo2,
  Download,
  Filter,
  Maximize2,
  Minimize2,
  Calendar,
  Clock,
} from "lucide-react";
import { api, formatApiError } from "../api/client";

const todayISO = () => new Date().toISOString().slice(0, 10);

// Human "X seconds ago" for the Live indicator. Small on purpose — we only
// need coarse feedback that data is fresh.
const formatRelativeTime = (d) => {
  if (!d) return "";
  const secs = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (secs < 5) return "just now";
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
};

// A single StatCard used in the top-of-page live counter row.
const StatCard = ({ label, value, sub, tone = "default", testId }) => {
  const bg =
    tone === "success"
      ? "var(--semantic-success-soft)"
      : tone === "warning"
      ? "var(--semantic-warning-soft)"
      : tone === "info"
      ? "var(--semantic-info-soft)"
      : "var(--surface-card)";
  const color =
    tone === "success"
      ? "var(--semantic-success)"
      : tone === "warning"
      ? "var(--semantic-warning)"
      : tone === "info"
      ? "var(--semantic-info)"
      : "var(--brand-primary)";
  return (
    <div
      data-testid={testId}
      className="rounded-lg border px-3 py-2.5 flex-1 min-w-[7.5rem]"
      style={{ background: bg, borderColor: "var(--border-default)" }}
    >
      <div className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: "var(--ink-tertiary)" }}>{label}</div>
      <div className="text-2xl font-bold leading-tight" style={{ color }}>{value}</div>
      {sub && <div className="text-[11px]" style={{ color: "var(--ink-tertiary)" }}>{sub}</div>}
    </div>
  );
};

// Confirmation dialog asking whether a guest is attending.
const GuestPromptModal = ({ attendee, onCancel, onConfirm, saving }) => {
  if (!attendee) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" style={{ background: "rgba(11,18,20,0.55)" }} data-testid="checkin-guest-modal">
      <div className="w-full max-w-md rounded-xl overflow-hidden shadow-xl" style={{ background: "var(--surface-card)", border: "1px solid var(--border-default)" }}>
        <div className="px-5 pt-4 pb-3 flex items-start gap-3" style={{ borderBottom: "1px solid var(--border-default)" }}>
          <div className="flex-shrink-0 h-11 w-11 rounded-md font-bold text-lg flex items-center justify-center" style={{ background: "var(--status-funds-bg)", color: "var(--status-funds-text)" }}>
            #{attendee.ticket_number}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate" style={{ color: "var(--ink-primary)" }}>{attendee.buyer || "(no buyer on file)"}</div>
            <div className="text-xs" style={{ color: "var(--ink-tertiary)" }}>{attendee.phone || "no phone"}</div>
          </div>
          <button type="button" onClick={onCancel} className="p-1 rounded" aria-label="Close" data-testid="checkin-guest-close">
            <X className="h-5 w-5" style={{ color: "var(--ink-tertiary)" }} />
          </button>
        </div>
        <div className="px-5 py-4">
          <p className="text-sm font-medium mb-3" style={{ color: "var(--ink-primary)" }}>
            Is a guest attending with this ticket?
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={() => onConfirm(true)}
              data-testid="checkin-with-guest-btn"
              className="px-3 py-3 rounded-md text-sm font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-60"
              style={{ background: "var(--brand-primary)" }}
            >
              <UserPlus2 className="inline h-4 w-4 mr-1.5 -mt-0.5" />
              Yes — +1 guest
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => onConfirm(false)}
              data-testid="checkin-no-guest-btn"
              className="px-3 py-3 rounded-md text-sm font-semibold transition-colors border hover:bg-[color:var(--surface-subtle)] disabled:opacity-60"
              style={{ borderColor: "var(--border-default)", color: "var(--ink-primary)", background: "var(--surface-card)" }}
            >
              <UserMinus2 className="inline h-4 w-4 mr-1.5 -mt-0.5" />
              No guest
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AttendeeRow = ({ a, kiosk, onTap, onUndo, isLastAction }) => {
  const rowBg = a.checked_in ? "var(--semantic-success-soft)" : "var(--surface-card)";
  const badgeBg = a.checked_in ? "var(--semantic-success)" : "var(--brand-primary)";
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onTap}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onTap(); } }}
      data-testid={`attendee-row-${a.ticket_number}`}
      data-checkedin={a.checked_in ? "1" : "0"}
      className={`flex items-center gap-3 px-3 py-3 border-b cursor-pointer transition-colors ${kiosk ? "sm:py-4" : ""}`}
      style={{ borderColor: "var(--border-default)", background: rowBg }}
    >
      <div
        className={`flex-shrink-0 rounded-md font-bold flex items-center justify-center ${kiosk ? "h-14 w-14 text-xl" : "h-11 w-11 text-base"}`}
        style={{ background: badgeBg, color: "#fff" }}
      >
        #{a.ticket_number}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-semibold truncate ${kiosk ? "text-lg" : "text-sm"}`} style={{ color: "var(--ink-primary)" }}>
          {a.buyer || <span style={{ color: "var(--ink-tertiary)" }}>(no buyer on file)</span>}
        </div>
        <div className={`${kiosk ? "text-sm" : "text-xs"}`} style={{ color: "var(--ink-tertiary)" }}>
          {a.phone || "no phone"}
          {a.assigned_seller_name ? <span className="mx-1.5">·</span> : null}
          {a.assigned_seller_name}
        </div>
      </div>
      {a.checked_in ? (
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <div className="flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold" style={{ background: "var(--semantic-success)", color: "#fff" }}>
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span>Checked in</span>
            {a.guest_attending && <span className="ml-0.5">+1</span>}
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onUndo(); }}
            data-testid={`checkin-undo-${a.ticket_number}`}
            className={`px-2 py-1 rounded border text-[11px] font-semibold ${isLastAction ? "ring-2 ring-offset-1" : ""}`}
            style={{ borderColor: "var(--border-default)", color: "var(--ink-secondary)", background: "var(--surface-card)" }}
            aria-label="Undo check-in"
          >
            <Undo2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div className={`text-xs font-semibold ${kiosk ? "text-sm" : ""}`} style={{ color: "var(--brand-primary)" }}>Tap to check in →</div>
      )}
    </div>
  );
};

export const CheckInTab = ({ currentUser, showToast }) => {
  const [drawings, setDrawings] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [attendees, setAttendees] = useState([]);
  const [stats, setStats] = useState({ total_eligible: 0, checked_in: 0, not_checked_in: 0, guests: 0, total_heads: 0 });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [query, setQuery] = useState("");
  const [showOnlyNotCheckedIn, setShowOnlyNotCheckedIn] = useState(false);
  const [kiosk, setKiosk] = useState(false);
  const [prompt, setPrompt] = useState(null); // attendee awaiting guest choice
  const [saving, setSaving] = useState(false);
  const [lastAction, setLastAction] = useState(null); // {ticket_number, action: 'checkin'|'undo'}
  const searchRef = useRef(null);
  const inflightRef = useRef(false);

  const isAdmin = currentUser?.role === "admin";

  // Load available drawings for the current year on mount.
  const fetchDrawings = useCallback(async () => {
    try {
      const { data } = await api.get("/checkin/drawings");
      setDrawings(data || []);
      // Auto-pick the best default: prefer today's drawing, else the closest upcoming one, else the last one.
      if ((data || []).length > 0 && !selectedId) {
        const t = todayISO();
        const today = data.find((d) => d.drawing_date === t);
        const upcoming = [...data].filter((d) => d.drawing_date >= t).sort((a, b) => a.drawing_date.localeCompare(b.drawing_date))[0];
        const pick = today || upcoming || data[data.length - 1];
        setSelectedId(pick.id);
      }
    } catch (e) {
      showToast?.(formatApiError(e) || "Could not load drawings");
    }
  }, [showToast, selectedId]);

  // Fetch attendees. `silent=true` skips the full-page loading state so the
  // background polling doesn't blank the list on every tick.
  const fetchAttendees = useCallback(async (drawingId, { silent = false } = {}) => {
    if (!drawingId || inflightRef.current) return;
    inflightRef.current = true;
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const { data } = await api.get(`/drawings/${drawingId}/checkin/attendees`);
      setAttendees(data.attendees || []);
      setStats(data.stats || { total_eligible: 0, checked_in: 0, not_checked_in: 0, guests: 0, total_heads: 0 });
      setLastUpdatedAt(new Date());
    } catch (e) {
      // Never toast on background polls — a transient network hiccup on drawing
      // night must not spam volunteers with error banners.
      if (!silent) {
        showToast?.(formatApiError(e) || "Could not load attendees");
        setAttendees([]);
      }
    } finally {
      inflightRef.current = false;
      if (silent) setRefreshing(false); else setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchDrawings(); }, [fetchDrawings]);
  useEffect(() => { fetchAttendees(selectedId); }, [selectedId, fetchAttendees]);

  // Auto-refresh: poll the attendee list every 4s so multiple volunteers see
  // each other's check-ins in real time. Skip when tab hidden or modal open.
  useEffect(() => {
    if (!selectedId) return undefined;
    const POLL_MS = 4000;
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      if (prompt) return; // avoid re-render while a decision is being made
      fetchAttendees(selectedId, { silent: true });
    };
    const id = setInterval(tick, POLL_MS);
    const onFocus = () => tick();
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [selectedId, prompt, fetchAttendees]);

  // Keep the search input focused so keyboard/scanner input flows naturally.
  useEffect(() => {
    if (!prompt) searchRef.current?.focus();
  }, [prompt, kiosk, selectedId]);

  // Tick every 15s so the "updated Xs ago" label stays honest even between polls.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    if (!lastUpdatedAt) return undefined;
    const id = setInterval(() => setNowTick((n) => n + 1), 15000);
    return () => clearInterval(id);
  }, [lastUpdatedAt]);

  const selectedDrawing = useMemo(
    () => drawings.find((d) => d.id === selectedId),
    [drawings, selectedId]
  );

  // Recent arrivals feed — last 10 check-ins, newest first. Powers the
  // "who just showed up" strip so volunteers don't have to scan the full
  // attendee list.
  const recentArrivals = useMemo(() => {
    return attendees
      .filter((a) => a.checked_in && a.checked_in_at)
      .sort((a, b) => b.checked_in_at.localeCompare(a.checked_in_at))
      .slice(0, 10);
  }, [attendees]);

  // Fuzzy client-side search across ticket #, buyer name, or phone.
  const filteredAttendees = useMemo(() => {
    const q = query.trim().toLowerCase();
    const digitsQ = q.replace(/\D+/g, "");
    let list = attendees;
    if (showOnlyNotCheckedIn) list = list.filter((a) => !a.checked_in);
    if (!q) return list;
    return list.filter((a) => {
      const nameMatch = a.buyer && a.buyer.toLowerCase().includes(q);
      const numMatch = digitsQ && String(a.ticket_number).includes(digitsQ);
      const phoneMatch = digitsQ && (a.phone || "").replace(/\D+/g, "").includes(digitsQ);
      return nameMatch || numMatch || phoneMatch;
    });
  }, [attendees, query, showOnlyNotCheckedIn]);

  const handleTap = (a) => {
    if (a.checked_in) {
      // Already checked in — do nothing on tap (use the undo button instead).
      return;
    }
    setPrompt(a);
  };

  const doCheckIn = async (guestAttending) => {
    if (!prompt || !selectedId) return;
    setSaving(true);
    try {
      await api.post(`/drawings/${selectedId}/checkin`, {
        ticket_number: prompt.ticket_number,
        guest_attending: guestAttending,
      });
      showToast?.(`Ticket #${prompt.ticket_number} checked in${guestAttending ? " (+1 guest)" : ""}`);
      setLastAction({ ticket_number: prompt.ticket_number, action: "checkin" });
      setQuery("");
      setPrompt(null);
      await fetchAttendees(selectedId);
    } catch (e) {
      showToast?.(formatApiError(e) || "Could not check in");
    } finally {
      setSaving(false);
    }
  };

  const doUndo = async (ticketNumber) => {
    if (!selectedId) return;
    try {
      await api.delete(`/drawings/${selectedId}/checkin/${ticketNumber}`);
      showToast?.(`Ticket #${ticketNumber} check-in removed`);
      setLastAction({ ticket_number: ticketNumber, action: "undo" });
      await fetchAttendees(selectedId);
    } catch (e) {
      showToast?.(formatApiError(e) || "Could not undo check-in");
    }
  };

  const exportCsv = () => {
    if (!attendees.length) return;
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ["Ticket", "Buyer", "Phone", "Assigned Seller", "Checked In", "Guest", "Time", "By"].join(",");
    const rows = attendees.map((a) => [
      a.ticket_number,
      a.buyer,
      a.phone,
      a.assigned_seller_name,
      a.checked_in ? "yes" : "no",
      a.checked_in && a.guest_attending ? "yes" : "no",
      a.checked_in_at,
      a.checked_in_by_name,
    ].map(esc).join(","));
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `checkin-${selectedDrawing?.drawing_date || "drawing"}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Enter in the search box: if exactly one match remains, open the guest prompt for it.
  // Escape clears the query — matches the muscle memory admins get from every OS-level search field.
  const onSearchKey = (e) => {
    if (e.key === "Enter" && filteredAttendees.length === 1) {
      const a = filteredAttendees[0];
      if (!a.checked_in) setPrompt(a);
    } else if (e.key === "Escape" && query) {
      e.preventDefault();
      setQuery("");
    }
  };

  const clearFilters = () => {
    setQuery("");
    setShowOnlyNotCheckedIn(false);
    searchRef.current?.focus();
  };

  const hasActiveFilter = query.length > 0 || showOnlyNotCheckedIn;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 space-y-4" data-testid="checkin-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: "var(--ink-primary)" }}>
            <Users2 className="inline h-5 w-5 mr-1.5 -mt-0.5" style={{ color: "var(--brand-primary)" }} />
            Drawing Check-In
          </h1>
          <p className="text-sm mt-0.5" style={{ color: "var(--ink-tertiary)" }}>
            Track who arrives on drawing night. Search by ticket #, name, or phone. Each ticket allows one guest.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setKiosk((k) => !k)}
            data-testid="checkin-kiosk-toggle"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border transition-colors hover:bg-[color:var(--surface-subtle)]"
            style={{ borderColor: "var(--border-default)", color: "var(--ink-secondary)", background: "var(--surface-card)" }}
            title="Toggle large-text mode"
          >
            {kiosk ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            {kiosk ? "Standard view" : "Kiosk mode"}
          </button>
          <button
            type="button"
            onClick={exportCsv}
            disabled={!attendees.length}
            data-testid="checkin-export-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border transition-colors hover:bg-[color:var(--surface-subtle)] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ borderColor: "var(--border-default)", color: "var(--ink-secondary)", background: "var(--surface-card)" }}
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Drawing picker */}
      <div className="rounded-lg border p-3 flex flex-wrap items-center gap-3" style={{ background: "var(--surface-card)", borderColor: "var(--border-default)" }}>
        <label htmlFor="checkin-drawing-select" className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5" style={{ color: "var(--ink-tertiary)" }}>
          <Calendar className="h-4 w-4" /> Drawing
        </label>
        <select
          id="checkin-drawing-select"
          data-testid="checkin-drawing-select"
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          className="rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2"
          style={{
            background: "var(--surface-card)",
            borderColor: "var(--border-default)",
            color: "var(--ink-primary)",
          }}
        >
          <option value="" disabled style={{ background: "var(--surface-card)", color: "var(--ink-primary)" }}>Select a drawing…</option>
          {drawings.map((d) => (
            <option
              key={d.id}
              value={d.id}
              style={{ background: "var(--surface-card)", color: "var(--ink-primary)" }}
            >
              {d.drawing_date} — {d.name} {d.drawing_type === "main" ? "(Main)" : "(Early Bird)"}
              {d.winning_ticket != null ? " · winner drawn" : ""}
            </option>
          ))}
        </select>
        {selectedDrawing && (
          <div className="text-xs" style={{ color: "var(--ink-tertiary)" }}>
            Prize: <strong style={{ color: "var(--ink-secondary)" }}>${Number(selectedDrawing.prize_amount).toFixed(2)}</strong>
          </div>
        )}
        {selectedId && (
          <div className="ml-auto flex items-center gap-1.5 text-xs" data-testid="checkin-live-indicator" style={{ color: "var(--ink-tertiary)" }}>
            <span
              aria-hidden="true"
              className={`inline-block w-2 h-2 rounded-full ${refreshing ? "checkin-live-dot-pulsing" : ""}`}
              style={{ background: "var(--semantic-success)" }}
            />
            <span>Live</span>
            {lastUpdatedAt && (
              <span className="hidden sm:inline">· updated {formatRelativeTime(lastUpdatedAt)}</span>
            )}
          </div>
        )}
      </div>

      {/* Live stats */}
      <div className="flex flex-wrap gap-2" data-testid="checkin-stats">
        <StatCard testId="checkin-stat-checked-in" label="Checked in" value={stats.checked_in} sub={`of ${stats.total_eligible} eligible`} tone="success" />
        <StatCard testId="checkin-stat-remaining" label="Not yet arrived" value={stats.not_checked_in} tone="warning" />
        <StatCard testId="checkin-stat-guests" label="Guests" value={stats.guests} tone="info" />
        <StatCard testId="checkin-stat-total-heads" label="Total heads" value={stats.total_heads} sub="attendees + guests" />
      </div>

      {/* Recent arrivals — live feed of the last 10 check-ins so volunteers can
          see who just walked in without hunting the full list. */}
      {selectedId && recentArrivals.length > 0 && (
        <div
          className="rounded-lg border p-3"
          style={{ background: "var(--surface-card)", borderColor: "var(--border-default)" }}
          data-testid="checkin-recent-arrivals"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5" style={{ color: "var(--ink-tertiary)" }}>
              <Clock className="h-3.5 w-3.5" />
              Recent arrivals
            </div>
            <div className="text-[10px]" style={{ color: "var(--ink-tertiary)" }}>Newest first · last {recentArrivals.length}</div>
          </div>
          <ul className="space-y-1.5">
            {recentArrivals.map((a) => (
              <li
                key={`recent-${a.ticket_number}-${a.checked_in_at}`}
                data-testid={`recent-arrival-${a.ticket_number}`}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md"
                style={{ background: "var(--semantic-success-soft)" }}
              >
                <div
                  className="flex-shrink-0 h-7 w-7 rounded-md text-xs font-bold flex items-center justify-center"
                  style={{ background: "var(--semantic-success)", color: "#fff" }}
                >
                  #{a.ticket_number}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate" style={{ color: "var(--ink-primary)" }}>
                    {a.buyer || <span style={{ color: "var(--ink-tertiary)" }}>(no buyer)</span>}
                    {a.guest_attending && (
                      <span
                        className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full align-middle"
                        style={{ background: "var(--semantic-info)", color: "#fff" }}
                      >
                        <UserPlus2 className="h-2.5 w-2.5" />
                        +1
                      </span>
                    )}
                  </div>
                  <div className="text-[11px]" style={{ color: "var(--ink-tertiary)" }}>
                    {formatRelativeTime(new Date(a.checked_in_at))}
                    {a.checked_in_by_name && (
                      <>
                        <span className="mx-1">·</span>
                        by {a.checked_in_by_name}
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Search + filter */}
      <div className="rounded-lg border p-3 space-y-2" style={{ background: "var(--surface-card)", borderColor: "var(--border-default)" }}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 pointer-events-none" style={{ color: "var(--ink-tertiary)" }} />
          <input
            ref={searchRef}
            data-testid="checkin-search-input"
            type="search"
            inputMode="search"
            autoFocus
            placeholder="Search by ticket #, name, or phone…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onSearchKey}
            className={`w-full rounded-md border pl-10 pr-10 py-3 focus:outline-none focus:ring-2 ${kiosk ? "text-lg py-4" : "text-base"}`}
            style={{ borderColor: "var(--border-default)", background: "var(--surface-card)", color: "var(--ink-primary)" }}
          />
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(""); searchRef.current?.focus(); }}
              data-testid="checkin-search-clear"
              aria-label="Clear search"
              title="Clear search (Esc)"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 inline-flex items-center justify-center rounded-md transition-colors hover:bg-[color:var(--surface-subtle)]"
              style={{ color: "var(--ink-tertiary)" }}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
          <label className="inline-flex items-center gap-2 cursor-pointer select-none" style={{ color: "var(--ink-secondary)" }} data-testid="checkin-filter-notyet">
            <input
              type="checkbox"
              checked={showOnlyNotCheckedIn}
              onChange={(e) => setShowOnlyNotCheckedIn(e.target.checked)}
              className="h-4 w-4"
            />
            <Filter className="h-3.5 w-3.5" />
            Show only not-yet-arrived
          </label>
          <div className="flex items-center gap-2" style={{ color: "var(--ink-tertiary)" }}>
            <span>{filteredAttendees.length} shown · Enter to check in when 1 match</span>
            {hasActiveFilter && (
              <button
                type="button"
                onClick={clearFilters}
                data-testid="checkin-clear-filters"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border font-semibold transition-colors hover:border-[color:var(--brand-primary)] hover:text-[color:var(--brand-primary)]"
                style={{ borderColor: "var(--border-default)", color: "var(--ink-secondary)", background: "var(--surface-card)" }}
                title="Clear search and filters"
              >
                <X className="h-3 w-3" />
                Reset
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Attendee list */}
      <div className="rounded-lg border overflow-hidden" style={{ background: "var(--surface-card)", borderColor: "var(--border-default)" }}>
        {!selectedId ? (
          <div className="p-6 text-center text-sm" style={{ color: "var(--ink-tertiary)" }}>
            Pick a drawing above to begin check-in.
          </div>
        ) : loading ? (
          <div className="p-6 text-center text-sm" style={{ color: "var(--ink-tertiary)" }} data-testid="checkin-loading">Loading attendees…</div>
        ) : filteredAttendees.length === 0 ? (
          <div className="p-6 text-center text-sm" style={{ color: "var(--ink-tertiary)" }} data-testid="checkin-empty">
            {attendees.length === 0
              ? "No eligible (funds-received) tickets for this drawing yet."
              : query
              ? `No matches for “${query}”.`
              : "Nobody left — everyone eligible has checked in."}
          </div>
        ) : (
          <div data-testid="checkin-attendee-list">
            {filteredAttendees.map((a) => (
              <AttendeeRow
                key={a.ticket_number}
                a={a}
                kiosk={kiosk}
                isLastAction={lastAction?.ticket_number === a.ticket_number && lastAction?.action === "checkin"}
                onTap={() => handleTap(a)}
                onUndo={() => doUndo(a.ticket_number)}
              />
            ))}
          </div>
        )}
      </div>

      {isAdmin && stats.total_eligible === 0 && selectedDrawing && (
        <p className="text-xs" style={{ color: "var(--ink-tertiary)" }}>
          Tip: No eligible tickets means no ticket was marked <em>Funds Received</em> on or before {selectedDrawing.drawing_date}. Admins can still record walk-in check-ins by paying and marking a ticket first.
        </p>
      )}

      <GuestPromptModal
        attendee={prompt}
        onCancel={() => setPrompt(null)}
        onConfirm={doCheckIn}
        saving={saving}
      />
    </section>
  );
};
